import React, { useEffect, useState, useCallback } from 'react';
import IntlMessages from '@iso/components/utility/intlMessages';
import { Button, Input, Row, Col, InputNumber, Form, Checkbox, Card, Collapse, Avatar, Space, Divider, List, Spin, Empty } from 'antd';
import { UserOutlined, BellOutlined, SettingOutlined, ArrowDownOutlined, ArrowUpOutlined, HistoryOutlined } from '@ant-design/icons';
import moment from 'moment';
import EditSensorNameModalWrapper from './EditSensorNameModal.styles';
import { sensorTypeSettingService } from '../../../services/sensorTypeSetting.service';

const { Panel } = Collapse;
const { TextArea } = Input;

// Booleans are stored as 0/1; showing "0 -> 1" for an SMS toggle is not an
// answer anybody wants to read.
const formatChangeValue = (change) => {
    const isFlag = change.field?.startsWith('is_send_');
    const render = (value) => {
        if (value === null || value === undefined || value === '') {
            return isFlag ? 'off' : 'not set';
        }
        return isFlag ? (Number(value) ? 'on' : 'off') : value;
    };

    return `${render(change.from)} → ${render(change.to)}`;
};

const renderChange = (change, index) => {
    // Recipient added/removed already reads as a sentence; the ids either
    // side of the arrow would add nothing.
    if (change.field === 'recipient_added' || change.field === 'recipient_removed') {
        return (
            <div key={index}>
                {change.label}: <strong>{change.user_name || `User ${change.user_id}`}</strong>
            </div>
        );
    }

    return (
        <div key={index}>
            {change.label}{change.user_name ? ` – ${change.user_name}` : ''}: {formatChangeValue(change)}
        </div>
    );
};

const EditSensorNameModal = (props) => {
    const { 
        sensor, 
        device,
        deviceField,
        visible, 
        onEditSensorNameSaved, 
        onCancel,
        typeModalSensor, 
        sensorType,
        statsSensor,
        usersProperty
    } = props;
    const [form] = Form.useForm();
    const [userSettings, setUserSettings] = useState([]);
    const [typeSetting, setTypeSetting] = useState({});
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [alarmHistory, setAlarmHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyKeys, setHistoryKeys] = useState([]);

    useEffect(() => {
        if (visible) {
            if (typeModalSensor === 'edit_device_name' || typeModalSensor === 'edit_device_description') {
                initializeDeviceForm();
            } else if (sensor) {
                initializeForm();
            }
        }
    }, [visible, sensor, device, typeModalSensor, statsSensor]);

    useEffect(() => {
        form.setFieldsValue({ users: userSettings });
    }, [userSettings]);

    // Reset the panel whenever the modal closes. The modal is mounted for the
    // life of the page (Sensors.js renders it unconditionally, and antd keeps
    // a closed Modal's children mounted), so nothing here resets itself —
    // including the Collapse's own expanded state, which is why this has to
    // drive it rather than leave it uncontrolled.
    useEffect(() => {
        if (!visible) {
            setAlarmHistory([]);
            setHistoryKeys([]);
            setSettingsLoaded(false);
        }
    }, [visible]);

    const initializeDeviceForm = () => {
        const fieldValue = typeModalSensor === 'edit_device_name' ? 
            (device?.device_name || '') : 
            (device?.device_description || '');
            
        form.setFieldsValue({
            [deviceField]: fieldValue,
            device_id: device?.id || '',
        });
    };

    const initializeForm = () => {
        form.setFieldsValue({
            name: sensor.name || '',
            sensor_id: sensor.id || '',
            type: sensorType || '',
            max_threshold: statsSensor ? Math.round(statsSensor.max) : 0,
            min_threshold: statsSensor ? Math.round(statsSensor.min) : 0,
        });
        getSensorTypeSetting(sensor.id);
    };

    const getSensorTypeSetting = (sensorId) => {
        sensorTypeSettingService.getList(sensorId).then(res => {
            if (res.code === '0000') {
                setSettingsLoaded(true);
                const typeSetting = res.sensor_type_settings.find(setting => setting.type === sensorType);
                setTypeSetting(typeSetting);
                const initialUserSettings = typeSetting && typeSetting.user_settings.map(userSetting => ({
                    id: userSetting.user.id,
                    is_send_app: !!userSetting.is_send_app ,
                    is_send_email: !!userSetting.is_send_email,
                    is_send_sms: !!userSetting.is_send_sms,
                }));

                form.setFieldsValue({
                    max_threshold: typeSetting?.max_threshold,
                    min_threshold: typeSetting?.min_threshold,
                });
                setUserSettings(initialUserSettings || []);
            }
        })
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        
        if (typeModalSensor === 'edit') {
            onEditSensorNameSaved(values.name);
        } else if (typeModalSensor === 'edit_device_name') {
            onEditSensorNameSaved(values.name);
        } else if (typeModalSensor === 'edit_device_description') {
            onEditSensorNameSaved(values.description);
        } else if (typeModalSensor === 'alarm') {
            onStoreOrUpdateTypeSetting(values);
        }
    };

const onStoreOrUpdateTypeSetting = (values) => {
    // Always use the existing typeSetting.id if it exists
    const id = typeSetting?.id || null;
    
    // Clean up the users array - remove any string values and ensure proper format
    const transformedUsers = values.users?.filter(user => 
        typeof user === 'object' && user.id
    ).map(user => ({
        id: parseInt(user.id),
        is_send_app: user.is_send_app,
        is_send_email: user.is_send_email,
        is_send_sms: user.is_send_sms,
    })) || [];
    
    const data = {
        sensor_type_setting: {
            ...values,
            users: transformedUsers
        }
    };

    // An empty list now clears every recipient server-side, so never submit one
    // we did not actually load: saving before the existing recipients arrive
    // would silently delete them.
    if (!settingsLoaded) {
        delete data.sensor_type_setting.users;
    }
    
    sensorTypeSettingService.storeOrUpdate(data, id).then(res => {
        if (res.code === '0000') {
            onCancel();
        }
    }).catch(error => {
        console.error('API Error:', error);
    });
}

    // Fetched on expand rather than on open: most people opening this modal are
    // changing a threshold, not auditing one. Every expand refetches — saving
    // closes the modal, so a cache held for the component's lifetime would
    // show the user their own change missing until they reloaded the page.
    const loadAlarmHistory = useCallback((activeKeys) => {
        const keys = Array.isArray(activeKeys) ? activeKeys : (activeKeys ? [activeKeys] : []);

        setHistoryKeys(keys);

        if (!keys.length || !sensor?.id) {
            return;
        }

        setHistoryLoading(true);
        sensorTypeSettingService.getAlarmHistory(sensor.id)
            .then(activities => setAlarmHistory(activities))
            .catch(error => {
                console.error('Failed to load alarm settings history:', error);
                setAlarmHistory([]);
            })
            .finally(() => setHistoryLoading(false));
    }, [sensor]);

    const renderAlarmHistory = useCallback(() => (
        <Collapse ghost activeKey={historyKeys} onChange={loadAlarmHistory}>
            <Panel
                header={
                    <Space>
                        <HistoryOutlined />
                        <span>Change history</span>
                    </Space>
                }
                key="history"
            >
                {historyLoading ? (
                    <Spin size="small" />
                ) : alarmHistory.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No changes recorded yet"
                    />
                ) : (
                    <List
                        size="small"
                        dataSource={alarmHistory}
                        renderItem={activity => (
                            <List.Item>
                                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                    <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                                        {moment(activity.created_at).format('D MMM YYYY, h:mm a')}
                                        {' · '}
                                        {activity.user_name || 'Unknown user'}
                                    </span>
                                    {(activity.metadata?.changes || []).map(renderChange)}
                                </Space>
                            </List.Item>
                        )}
                    />
                )}
            </Panel>
        </Collapse>
    ), [alarmHistory, historyLoading, historyKeys, loadAlarmHistory]);

    const handleCancel = () => {
        onCancel();
    };

    const options = [
        { label: 'App', value: 'is_send_app' },
        { label: 'Email', value: 'is_send_email' },
        { label: 'SMS', value: 'is_send_sms' },
    ];

    // Get count of users with notifications enabled
    const getActiveUsersCount = () => {
        return userSettings?.filter(user => 
            user.is_send_app || user.is_send_email || user.is_send_sms
        ).length || 0;
    };

const renderUserCard = useCallback((user) => {
    const userValues = userSettings?.find(u => u.id === user.id) || {};
    const selectedValues = [
        ...(userValues.is_send_app ? ['is_send_app'] : []),
        ...(userValues.is_send_email ? ['is_send_email'] : []),
        ...(userValues.is_send_sms ? ['is_send_sms'] : []),
    ];
    
    const hasNotifications = selectedValues.length > 0;
    
    // Handle card click - select all if none selected, deselect all if any selected
    const handleCardClick = (e) => {
        // Prevent card click when clicking on checkboxes
        if (e.target.closest('.ant-checkbox-group') || e.target.closest('.ant-checkbox-wrapper')) {
            return;
        }
        
        const newValues = hasNotifications ? [] : ['is_send_app', 'is_send_email', 'is_send_sms'];
        handleChangeCheckbox(user.id, newValues);
    };
    
    return (
        <Card 
            key={user.id}
            size="default" 
            className={`user-card ${hasNotifications ? 'active' : ''}`}
            style={{ 
                marginBottom: 8,
                cursor: 'pointer'
            }}
            onClick={handleCardClick}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <span style={{ fontWeight: hasNotifications ? 500 : 'normal' }}>
                        {user.name}
                    </span>
                </Space>
                <Space>
                    <Checkbox.Group
                        options={options}
                        value={selectedValues}
                        onChange={(selectedValues) => {
                            // Individual checkbox handling - only change the clicked checkbox
                            handleChangeCheckbox(user.id, selectedValues);
                        }}
                        size="small"
                        onClick={(e) => {
                            // Stop propagation to prevent card click
                            e.stopPropagation();
                        }}
                    />
                    {hasNotifications && (
                        <BellOutlined style={{ color: '#0B2A1B', fontSize: '12px' }} />
                    )}
                </Space>
            </div>
        </Card>
    );
}, [userSettings]);

    const renderUsers = useCallback(() => {
        // Split users into chunks for better layout
        const chunkSize = Math.ceil(usersProperty.length / 2);
        const userChunks = [];
        for (let i = 0; i < usersProperty.length; i += chunkSize) {
            userChunks.push(usersProperty.slice(i, i + chunkSize));
        }

        if (usersProperty.length <= 3) {
            // Show all users directly if there are few
            return (
                <div className="users-list">
                    {usersProperty.map(user => renderUserCard(user))}
                </div>
            );
        }

        // Use collapsible panel for many users
        return (
            <Collapse 
                ghost
                defaultActiveKey={getActiveUsersCount() > 0 ? ['1'] : []}
            >
                <Panel 
                    header={
                        <Space>
                            <UserOutlined />
                            <span>Notification Recipients ({getActiveUsersCount()}/{usersProperty.length} users)</span>
                        </Space>
                    } 
                    key="1"
                >
                    <Row gutter={[16, 0]}>
                        {userChunks.map((chunk, index) => (
                            <Col xs={24} sm={24} md={24} key={index}>
                                {chunk.map(user => renderUserCard(user))}
                            </Col>
                        ))}
                    </Row>
                </Panel>
            </Collapse>
        );
    }, [userSettings, usersProperty, renderUserCard]);
        
    const handleChangeCheckbox = (userId, selectedValues) => {
        const updatedUser = {
            id: userId,
            is_send_app: selectedValues.includes('is_send_app'),
            is_send_email: selectedValues.includes('is_send_email'),
            is_send_sms: selectedValues.includes('is_send_sms'),
        };

        // Get current users from state, not form
        let currentUsers = [...(userSettings || [])];

        // Always keep user in array (even with all false values) so backend knows to update
        const userIndex = currentUsers.findIndex(user => user.id === userId);
        if (userIndex > -1) {
            currentUsers[userIndex] = updatedUser;
        } else {
            currentUsers = [...currentUsers, updatedUser];
        }

        // Update both state and form
        setUserSettings(currentUsers);
        form.setFieldsValue({ users: currentUsers });
    };
    
    const renderContent = useCallback(() => {
        switch (typeModalSensor) {
            case 'edit':
                return (
                    <Form.Item
                        label="Sensor Name"
                        name="name"
                        rules={[{ required: true, message: 'Please input sensor name!' }]}
                    >
                        <Input size="large" />
                    </Form.Item>
                );
            case 'edit_device_name':
                return (
                    <>
                        <Form.Item name="device_id" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Device Name"
                            name="name"
                            rules={[{ required: true, message: 'Please input device name!' }]}
                        >
                            <Input size="large" placeholder="Enter device name" />
                        </Form.Item>
                    </>
                );
            case 'edit_device_description':
                return (
                    <>
                        <Form.Item name="device_id" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Device Description"
                            name="description"
                            rules={[{ max: 500, message: 'Description must be less than 500 characters!' }]}
                        >
                            <TextArea 
                                size="large" 
                                placeholder="Enter device description (optional)" 
                                rows={4}
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>
                    </>
                );
            case 'alarm':
                return (
                    <>
                        {/* Hidden fields - outside of Space to avoid empty spacing */}
                        <Form.Item name="sensor_id" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item name="type" hidden>
                            <Input />
                        </Form.Item>

                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                            {/* Threshold Settings Card */}
                        <Card 
                            title={
                                <Space>
                                    <SettingOutlined />
                                    <span>Threshold Settings</span>
                                </Space>
                            }
                            size="small"
                        >
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="min_threshold"
                                        label={
                                            <Space>
                                                <ArrowDownOutlined style={{ color: '#52c41a' }} />
                                                <span>Min Threshold</span>
                                            </Space>
                                        }
                                        rules={[
                                            { 
                                                required: true, 
                                                message: 'Please enter min threshold' 
                                            },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const maxValue = getFieldValue('max_threshold');
                                                    if (!value || !maxValue || value < maxValue) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Min threshold must be less than max threshold'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <InputNumber 
                                            addonAfter={statsSensor?.unit} 
                                            style={{width:'100%'}}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="max_threshold"
                                        label={
                                            <Space>
                                                <ArrowUpOutlined style={{ color: '#ff4d4f' }} />
                                                <span>Max Threshold</span>
                                            </Space>
                                        }
                                        rules={[
                                            { 
                                                required: true, 
                                                message: 'Please enter max threshold' 
                                            },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const minValue = getFieldValue('min_threshold');
                                                    if (!value || !minValue || value > minValue) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Max threshold must be greater than min threshold'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <InputNumber 
                                            addonAfter={statsSensor?.unit}  
                                            style={{width:'100%'}}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                        <hr style={{borderColor:'rgba(0, 0, 0, 0.15)', color: 'rgba(0, 0, 0, 0.15)'}}></hr>
                        {/* Users Notification Card */}
                        <Card 
                            title={
                                <Space>
                                    <BellOutlined style={{ color: '#0B2A1B'}}/>
                                    <span>Notification Settings</span>
                                </Space>
                            }
                            size="small"
                        >
                            <Form.Item name="users">
                                {renderUsers()}
                            </Form.Item>
                        </Card>
                        {renderAlarmHistory()}
                        </Space>
                    </>
                );
            default:
                return <></>;
        }
    }, [typeModalSensor, statsSensor, renderUsers, device, renderAlarmHistory]);
       
    const renderTitle = useCallback(() => {
        switch (typeModalSensor) {
            case 'edit':
                return 'Edit Sensor Name';
            case 'edit_device_name':
                return 'Edit Device Name';
            case 'edit_device_description':
                return 'Edit Device Description';
            case 'alarm':
                return 'Configure Alarm Settings';
            default:
                return '';
        }
    }, [typeModalSensor]);

    return (
        <EditSensorNameModalWrapper
            title={renderTitle()}
            open={visible}
            onCancel={handleCancel}
            onFinish={handleSave}
            width={typeModalSensor === 'alarm' ? 720 : 480}
            footer={[
                <Button key="cancel" className="cancel-button" onClick={handleCancel}>
                    <IntlMessages id="sensorPage.editSensorNameModal.cancel" />
                </Button>,
                <Button key="save" className="save-button" type="primary" onClick={handleSave}>
                    <IntlMessages id="sensorPage.editSensorNameModal.save" />
                </Button>
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                size="middle"
            >
                {renderContent()}
            </Form>
        </EditSensorNameModalWrapper>
    );
};

export default EditSensorNameModal;